import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { bmxurl, ConvertDateToDB, getAccess } from 'src/globals';
import { BusyService } from '../_services/busy.service';
import { GlobalService } from '../_services/global.service';
import { backtest, BacktestRow, band, Bands, bandsAt, BookingHistory, dayDate, dayNumber, DayWeather, Forecast, forecastDay, ForecastBooking, today } from './forecast';

//Where the venue is, for its weather and which set of bank holidays applies
interface Place {
  lat: number;
  lon: number;
  postcode: string;
  country: string | null;   //England, Wales, Scotland or Northern Ireland, from postcodes.io
}

/*
  Business Forecast (Beta) - the next fortnight's likely guests, and how well the same forecast would
  have done over the last 60 days. Read-only. See forecast.ts for how it is worked out.
*/

interface ForecastCard {
  forecast: Forecast;
  date: Date;
  bookedNow: number;        //everything booked so far, including today's bookings
  estimate: number | null;
  band: string | null;
  open: boolean;
}

//How far back bookings are read - 14 months, so the same week last year can be compared with the
//weeks around it. The booking list answers about 93 days at a time.
const HISTORY_DAYS = 430;
const AHEAD_DAYS = 14;
const CHUNK_DAYS = 90;
const BACKTEST_DAYS = 60;

@Component({
  selector: 'app-busyforecast',
  templateUrl: './busyforecast.component.html',
  styleUrls: ['./busyforecast.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BusyForecastComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;
  loaded: boolean = false;

  history: BookingHistory | null = null;
  bands: Bands | null = null;
  cards: ForecastCard[] = [];
  historyFrom: Date | null = null;
  openDays: number = 0;
  missingCreated: number = 0;
  failedLoads: string[] = [];

  leadOptions = [
    { label: '1 day before', value: 1 },
    { label: '3 days before', value: 3 },
    { label: '7 days before', value: 7 },
    { label: '14 days before', value: 14 }
  ];
  backtestLead: number = 7;
  showHelp: boolean = false;
  backtestRows: BacktestRow[] = [];
  backtestAverageMiss: number = 0;
  backtestAverageActual: number = 0;
  backtestWithin20: number = 0;
  backtestBandRight: number = 0;
  backtestBandRows: number = 0;
  chartData: any = null;
  chartOptions: any = {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'bottom' } },
    scales: { y: { beginAtZero: true, title: { display: true, text: 'Guests' } } }
  };

  //Where the weather was read for, to show on the page
  placeText: string = '';
  weatherDays: number = 0;

  /*
    Calls to outside services (weather, postcodes, bank holidays) skip the app's interceptors, which
    would add our venue, user and business ids to every request - those are not theirs to see, and
    Open-Meteo turns the extra headers away.
  */
  private outside: HttpClient;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService) {
    this.outside = new HttpClient(httpBackend);
  }

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      const httpResponse: any = response;

      if (httpResponse.succeeded) {
        this.AccessAllowed = getAccess("REPORTS", httpResponse.data) == true;

        if (this.AccessAllowed)
          this.load();
      }

      this.CompletedLoading = true;
    }, error => {
      this.CompletedLoading = true;
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User.'});
    });
  }

  load() {
    this.busyService.busy();

    const now = today();
    const firstDay = now - HISTORY_DAYS;
    const lastDay = now + AHEAD_DAYS - 1;

    //Chunks overlap by a day in case the end date is not included; bookings are kept once by id
    const chunks: Observable<any[]>[] = [];
    for (let start = firstDay; start <= lastDay; start += CHUNK_DAYS)
      chunks.push(this.loadBookings(start, Math.min(start + CHUNK_DAYS, lastDay)));

    //The weather and bank holidays both need to know where the venue is
    const outside = this.loadPlace().pipe(
      switchMap(place => forkJoin({
        weather: place == null ? of(new Map<number, DayWeather>()) : this.loadWeather(place, firstDay),
        bankHolidays: this.loadBankHolidays(place?.country ?? null, firstDay, lastDay)
      }))
    );

    forkJoin({ chunks: forkJoin(chunks), holidays: this.loadHolidays(firstDay, lastDay), outside }).subscribe(result => {
      result.outside.bankHolidays.forEach((names, day) => {
        const all = result.holidays.get(day) ?? [];
        names.forEach(n => { if (!all.includes(n)) all.push(n); });
        result.holidays.set(day, all);
      });
      this.weatherDays = result.outside.weather.size;

      const seen = new Set<string>();
      const bookings: ForecastBooking[] = [];
      this.missingCreated = 0;

      result.chunks.flat().forEach(b => {
        if (b == null || b.inProgress == true || seen.has(b.bookingId))
          return;
        seen.add(b.bookingId);

        const day = dayNumber(b.startTime);
        if (day == null || day < firstDay || day > lastDay)
          return;

        //A booking with no created date is counted as made on the day, so it never makes the pace look ahead
        let created = dayNumber(b.createddate);
        if (created == null || created < dayNumber('1901-01-01')!) {
          this.missingCreated++;
          created = day;
        }

        bookings.push({ day, created: Math.min(created, day), guests: Number(b.guests) || 0 });
      });

      this.history = new BookingHistory({ bookings, holidays: result.holidays, firstDay, weather: result.outside.weather });

      const learnable = this.history.learnableDays(now);
      this.openDays = learnable.length;
      this.historyFrom = learnable.length > 0 ? dayDate(learnable[learnable.length - 1]) : null;

      this.bands = bandsAt(this.history, now);
      this.cards = [];
      for (let day = now; day <= lastDay; day++) {
        const forecast = forecastDay(this.history, day, now);
        const bookedNow = this.history.final(day);
        const estimate = forecast.estimate == null ? null : Math.max(forecast.estimate, bookedNow);

        //The forecast works from bookings made before today, so the same sum can be checked against past days
        const today = bookedNow - forecast.bookedAtSnapshot;
        if (today > 0)
          forecast.reasons.push(today + (today == 1 ? ' more guest has' : ' more guests have') + ' booked today. Today\'s bookings are counted from tomorrow' +
            (forecast.estimate != null && bookedNow > forecast.estimate ? ', but the forecast is never below what is already booked (' + bookedNow + ').' : '.'));
        this.cards.push({ forecast, date: dayDate(day), bookedNow, estimate, band: band(this.bands, estimate), open: false });
      }

      this.runBacktest();
      this.loaded = true;
      this.busyService.idle();
    });
  }

  /*
    Where the venue is: its own Lat / Lon when set, otherwise its postcode looked up on postcodes.io.
    The postcode is looked up either way, as it also says which UK country - and so which bank
    holidays - the venue is in. Null when neither gives a place, and the forecast goes without weather.
  */
  loadPlace(): Observable<Place | null> {
    return this.http.get<any>(bmxurl + "venue/" + this.VenueID).pipe(
      catchError(() => of(null)),
      switchMap(response => {
        const venue = response?.data ?? {};
        const lat = Number(venue.Lat ?? venue.lat) || 0;
        const lon = Number(venue.Lon ?? venue.lon) || 0;
        const postcode = String(venue.Postcode ?? venue.postcode ?? '').trim();

        const lookup = postcode == '' ? of(null) : this.outside.get<any>("https://api.postcodes.io/postcodes/" + encodeURIComponent(postcode)).pipe(
          map(found => found?.result ?? null),
          catchError(() => of(null))
        );

        return lookup.pipe(map(found => {
          const place: Place | null = (lat != 0 || lon != 0)
            ? { lat, lon, postcode, country: found?.country ?? null }
            : found != null ? { lat: found.latitude, lon: found.longitude, postcode: found.postcode ?? postcode, country: found.country ?? null } : null;

          if (place == null)
            this.failedLoads.push('weather (the venue has no location or postcode that could be found)');
          else
            this.placeText = (place.postcode != '' ? place.postcode + ' ' : '') + '(' + place.lat.toFixed(2) + ', ' + place.lon.toFixed(2) + ')';

          return place;
        }));
      })
    );
  }

  /*
    Daily rain and high temperature from Open-Meteo: the archive for past days (it runs a few days
    behind), then the forecast API for the last fortnight and the next 16 days, which takes over
    where both have a day. Days are the venue's own, from timezone=auto.
  */
  loadWeather(place: Place, firstDay: number): Observable<Map<number, DayWeather>> {
    const where = "latitude=" + place.lat + "&longitude=" + place.lon + "&daily=precipitation_sum,temperature_2m_max&timezone=auto";
    const key = (day: number) => dayDate(day).toISOString().substring(0, 10);

    const archive = this.outside.get<any>("https://archive-api.open-meteo.com/v1/archive?" + where + "&start_date=" + key(firstDay) + "&end_date=" + key(today() - 3)).pipe(catchError(() => of(null)));
    const forecast = this.outside.get<any>("https://api.open-meteo.com/v1/forecast?" + where + "&past_days=14&forecast_days=16").pipe(catchError(() => of(null)));

    return forkJoin([archive, forecast]).pipe(map(answers => {
      const weather = new Map<number, DayWeather>();

      answers.forEach(answer => {
        const daily = answer?.daily;
        (daily?.time ?? []).forEach((time: string, i: number) => {
          const day = dayNumber(time);
          const rain = daily.precipitation_sum?.[i];
          const tmax = daily.temperature_2m_max?.[i];
          if (day != null && rain != null && tmax != null)
            weather.set(day, { rain: Number(rain), tmax: Number(tmax) });
        });
      });

      if (answers[0] == null || answers[1] == null)
        this.failedLoads.push(answers[1] == null ? 'weather forecast' : 'past weather');

      return weather;
    }));
  }

  //Bank holidays from gov.uk, for the venue's own part of the UK. Venues outside the UK get none.
  loadBankHolidays(country: string | null, firstDay: number, lastDay: number): Observable<Map<number, string[]>> {
    const division = country == null ? null
      : country == 'Scotland' ? 'scotland'
      : country == 'Northern Ireland' ? 'northern-ireland'
      : (country == 'England' || country == 'Wales') ? 'england-and-wales' : null;

    if (division == null)
      return of(new Map<number, string[]>());

    return this.outside.get<any>("https://www.gov.uk/bank-holidays.json").pipe(
      map(all => {
        const days = new Map<number, string[]>();
        (all?.[division]?.events ?? []).forEach((e: any) => {
          const day = dayNumber(e.date);
          if (day != null && day >= firstDay && day <= lastDay)
            days.set(day, [e.title ?? 'Bank holiday']);
        });
        return days;
      }),
      catchError(() => {
        this.failedLoads.push('bank holidays');
        return of(new Map<number, string[]>());
      })
    );
  }

  //One chunk of bookings by visit date. A failed chunk is noted and left out rather than stopping the page.
  loadBookings(fromDay: number, toDay: number): Observable<any[]> {
    const searchCrit = {
      venueid: this.VenueID,
      startdate: ConvertDateToDB(this.localDate(fromDay)),
      enddate: ConvertDateToDB(this.localDate(toDay)),
      tags: [],
      disabled: false,
      created: true
    };

    return this.http.post<any>(bmxurl + "/BookingList", searchCrit).pipe(
      map(response => {
        if (!response?.succeeded) {
          this.failedLoads.push('bookings from ' + dayDate(fromDay).toISOString().substring(0, 10));
          return [];
        }
        return response.data ?? [];
      }),
      catchError(() => {
        this.failedLoads.push('bookings from ' + dayDate(fromDay).toISOString().substring(0, 10));
        return of([]);
      })
    );
  }

  /*
    The holiday days: every date covered by a timeset other than the default. Each one is read in
    full, as Timeset Edit does, since the list may not carry the dates.
  */
  loadHolidays(firstDay: number, lastDay: number): Observable<Map<number, string[]>> {
    return this.http.get<any>(bmxurl + "TimesetList/" + this.VenueID + "/false").pipe(
      catchError(() => of(null)),
      map(response => {
        if (!response?.succeeded)
          this.failedLoads.push('time sets');
        return ((response?.succeeded ? response.data : null) ?? []).filter((t: any) => !t.isdefault && !t.disabled);
      }),
      switchMap((listed: any[]) => listed.length == 0 ? of([]) : forkJoin(listed.map(t =>
        this.http.get<any>(bmxurl + "timesets/" + this.VenueID + "/" + t.id).pipe(
          map(response => response?.succeeded && response.data != null ? response.data : t),
          catchError(() => of(t))
        )))),
      map((timesets: any[]) => {
        const holidays = new Map<number, string[]>();

        timesets.forEach(t => (t.holidayopenings ?? []).forEach((h: any) => {
          const from = dayNumber(h.dateFrom);
          const to = dayNumber(h.dateTo) ?? from;
          if (from == null || to == null)
            return;

          for (let day = Math.max(from, firstDay); day <= Math.min(to, lastDay); day++) {
            const names = holidays.get(day) ?? [];
            if (!names.includes(t.name))
              names.push(t.name);
            holidays.set(day, names);
          }
        }));

        return holidays;
      })
    );
  }

  runBacktest() {
    if (this.history == null)
      return;

    const now = today();
    this.backtestRows = backtest(this.history, now - BACKTEST_DAYS, now - 1, this.backtestLead);

    const rows = this.backtestRows;
    this.backtestAverageMiss = rows.length == 0 ? 0 : rows.reduce((t, r) => t + Math.abs(r.forecast - r.actual), 0) / rows.length;
    this.backtestAverageActual = rows.length == 0 ? 0 : rows.reduce((t, r) => t + r.actual, 0) / rows.length;
    this.backtestWithin20 = rows.length == 0 ? 0 : rows.filter(r => Math.abs(r.forecast - r.actual) <= 0.2 * r.actual).length / rows.length;

    const banded = rows.filter(r => r.forecastBand != null && r.actualBand != null);
    this.backtestBandRows = banded.length;
    this.backtestBandRight = banded.length == 0 ? 0 : banded.filter(r => r.forecastBand == r.actualBand).length / banded.length;

    this.chartData = {
      labels: rows.map(r => dayDate(r.day).toISOString().substring(8, 10) + '/' + dayDate(r.day).toISOString().substring(5, 7)),
      datasets: [
        { label: 'Actual guests', data: rows.map(r => r.actual), borderColor: '#2563eb', backgroundColor: '#2563eb', tension: 0.2 },
        { label: 'Forecast ' + this.backtestLead + (this.backtestLead == 1 ? ' day' : ' days') + ' before', data: rows.map(r => Math.round(r.forecast)), borderColor: '#f59e0b', backgroundColor: '#f59e0b', borderDash: [6, 4], tension: 0.2 }
      ]
    };
  }

  bandClass(name: string | null): string {
    return name == null ? 'bf-band-none' : 'bf-band-' + name.toLowerCase().replace(' ', '');
  }

  //The day as a local midnight, which ConvertDateToDB turns into the stored wall-clock-as-Z form
  private localDate(day: number): Date {
    const d = dayDate(day);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
}
